import { useRef } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@mui/material/styles'
import GlobalStyles from '@mui/material/GlobalStyles'
import Box from '@mui/material/Box'
import { Link, useTranslation } from 'gatsby-plugin-react-i18next'
import { Helmet } from 'react-helmet'
import CookieConsent from 'react-cookie-consent'

import { useOnMount } from 'hooks'
import SocialLinks from 'components/SocialLinks'
import ColorModeSwitcher from 'components/ColorModeSwitcher'
import LanguageSwitcher from 'components/LanguageSwitcher'
import Footer from 'components/Footer'
import logo from 'assets/logo.svg'

import {
  globalStyle,
  rootStyle,
  navStyle,
  navEndStyle,
  logoStyle,
  logoIconStyle,
  logoTypeStyle
} from './BaseLayout.styles'

function BaseLayout({ children, showFooter }) {
  const theme = useTheme()
  const { t } = useTranslation()

  const navRef = useRef()

  const initialScrollEventRef = useRef(true)
  const scrollFrameComingRef = useRef(false)
  const prevScrollPosRef = useRef(0)

  useOnMount(() => {
    if (window) {
      const nav = navRef.current

      const onScroll = () => {
        if (initialScrollEventRef.current) {
          initialScrollEventRef.current = false
          return
        }

        if (!initialScrollEventRef.current && !scrollFrameComingRef.current) {
          window.requestAnimationFrame(() => {
            const currentScrollPos = window.pageYOffset

            if (Math.abs(currentScrollPos - prevScrollPosRef.current) > 20) {
              if (
                prevScrollPosRef.current < currentScrollPos &&
                nav.offsetHeight < currentScrollPos
              ) {
                nav.style.transform = 'translateY(-100%)'
                nav.style.opacity = 0
              } else {
                nav.style.transform = 'translateY(0)'
                nav.style.opacity = 1
              }
            }
            scrollFrameComingRef.current = false
            prevScrollPosRef.current = currentScrollPos
          })

          scrollFrameComingRef.current = true
        }
      }

      window.addEventListener('scroll', onScroll)

      return () => window.removeEventListener('scroll', onScroll)
    }
  })

  return (
    <>
      <Helmet>
        <meta name="theme-color" content={theme.palette.background.paper} />
      </Helmet>
      <GlobalStyles styles={globalStyle} />
      <Box sx={rootStyle}>
        <Box component="nav" ref={navRef} sx={navStyle}>
          <Box component={Link} to="/" sx={logoStyle}>
            <Box component="img" src={logo} alt="Logo" sx={logoIconStyle} />
            <Box component="span" sx={logoTypeStyle}>Zuyet Awarmatrip</Box>
          </Box>
          <Box sx={navEndStyle}>
            <SocialLinks shownKeys={['instagram']} />
            <ColorModeSwitcher />
            <LanguageSwitcher />
          </Box>
        </Box>
        <Box>{children}</Box>
        {showFooter && <Footer />}
        <CookieConsent
          contentClasses="cookie-consent-content"
          buttonClasses="accept-cookie-btn"
          buttonText={t('cookie.accept')}
        >
          {t('cookie.message')}
        </CookieConsent>
      </Box>
    </>
  )
}

BaseLayout.propTypes = {
  children: PropTypes.node,
  showFooter: PropTypes.bool
}

BaseLayout.defaultProps = {
  showFooter: true
}

export default BaseLayout
